/**
 * Utilitaires pour Background Sync
 * Gère la synchronisation en arrière-plan des données API et des favoris
 */

// Constantes pour Background Sync
export const SYNC_TAGS = {
  WAIT_TIMES: 'wait-times-sync',
  FAVORITES: 'favorites-sync',
  CACHE_UPDATE: 'cache-update-sync'
};

// Clés de stockage
const FAILED_REQUESTS_KEY = 'mpt-failed-requests';
const PENDING_FAVORITES_KEY = 'mpt-pending-favorites';
const SYNC_STATUS_KEY = 'mpt-sync-status';

/**
 * Vérifie si Background Sync est supporté
 */
export const isBackgroundSyncSupported = () => {
  return 'serviceWorker' in navigator && 'SyncManager' in window;
};

/**
 * Enregistre une requête API échouée pour synchronisation ultérieure
 */
export const registerFailedRequest = (url, method = 'GET', headers = {}, body = null) => {
  if (!isBackgroundSyncSupported()) {
    console.log('[Background Sync] Non supporté, requête ignorée:', url);
    return;
  }

  try {
    const failedRequests = getFailedRequests();
    
    // Éviter les doublons
    const exists = failedRequests.some(req => req.url === url && req.method === method);
    if (exists) {
      console.log('[Background Sync] Requête déjà enregistrée:', url);
      return;
    }

    const newRequest = {
      url,
      method,
      headers,
      body,
      timestamp: Date.now(),
      retryCount: 0
    };

    failedRequests.push(newRequest);
    localStorage.setItem(FAILED_REQUESTS_KEY, JSON.stringify(failedRequests));
    
    console.log('[Background Sync] Requête échouée enregistrée:', url);
    
    // Enregistrer le sync
    requestBackgroundSync(SYNC_TAGS.WAIT_TIMES);
    
  } catch (error) {
    console.error('[Background Sync] Erreur lors de l\'enregistrement de la requête échouée:', error);
  }
};

/**
 * Récupère les requêtes échouées depuis le localStorage
 */
export const getFailedRequests = () => {
  try {
    const stored = localStorage.getItem(FAILED_REQUESTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('[Background Sync] Erreur lors de la récupération des requêtes échouées:', error);
    return [];
  }
};

/**
 * Supprime les requêtes réussies du stockage
 */
export const removeFailedRequests = (urls) => {
  try {
    const failedRequests = getFailedRequests();
    const filteredRequests = failedRequests.filter(req => !urls.includes(req.url));
    localStorage.setItem(FAILED_REQUESTS_KEY, JSON.stringify(filteredRequests));
    console.log('[Background Sync] Requêtes supprimées:', urls.length);
  } catch (error) {
    console.error('[Background Sync] Erreur lors de la suppression des requêtes:', error);
  }
};

/**
 * Enregistre un changement de favori en attente de synchronisation
 */
export const registerPendingFavorite = (action, attractionId, attractionName, parkId) => {
  if (!isBackgroundSyncSupported()) {
    console.log('[Background Sync] Non supporté, favori ignoré');
    return;
  }

  try {
    const pendingFavorites = getPendingFavorites();
    
    const newFavorite = {
      action, // 'add' ou 'remove'
      attractionId,
      attractionName,
      parkId,
      timestamp: Date.now()
    };

    pendingFavorites.push(newFavorite);
    localStorage.setItem(PENDING_FAVORITES_KEY, JSON.stringify(pendingFavorites));
    
    console.log('[Background Sync] Favori en attente enregistré:', newFavorite);
    
    // Enregistrer le sync
    requestBackgroundSync(SYNC_TAGS.FAVORITES);
    
  } catch (error) {
    console.error('[Background Sync] Erreur lors de l\'enregistrement du favori en attente:', error);
  }
};

/**
 * Récupère les favoris en attente de synchronisation
 */
export const getPendingFavorites = () => {
  try {
    const stored = localStorage.getItem(PENDING_FAVORITES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('[Background Sync] Erreur lors de la récupération des favoris en attente:', error);
    return [];
  }
};

/**
 * Nettoie les favoris en attente après synchronisation
 */
export const clearPendingFavorites = () => {
  try {
    localStorage.removeItem(PENDING_FAVORITES_KEY);
    console.log('[Background Sync] Favoris en attente nettoyés');
  } catch (error) {
    console.error('[Background Sync] Erreur lors du nettoyage des favoris en attente:', error);
  }
};

/**
 * Demande une synchronisation en arrière-plan
 */
export const requestBackgroundSync = async (tag) => {
  if (!isBackgroundSyncSupported()) {
    console.log('[Background Sync] Non supporté pour le tag:', tag);
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if (!registration?.sync || typeof registration.sync.register !== 'function') {
      return false;
    }
    await registration.sync.register(tag);
    
    // Mettre à jour le statut de sync
    updateSyncStatus(tag, 'requested');
    
    console.log('[Background Sync] Synchronisation demandée pour:', tag);
    return true;
    
  } catch (error) {
    if (error?.name === 'NotAllowedError') {
      return false;
    }
    console.error('[Background Sync] Erreur lors de la demande de synchronisation:', error);
    return false;
  }
};

/**
 * Met à jour le statut de synchronisation
 */
export const updateSyncStatus = (tag, status, data = null) => {
  try {
    const syncStatus = getSyncStatus();
    syncStatus[tag] = {
      status,
      timestamp: Date.now(),
      data
    };
    localStorage.setItem(SYNC_STATUS_KEY, JSON.stringify(syncStatus));
  } catch (error) {
    console.error('[Background Sync] Erreur lors de la mise à jour du statut:', error);
  }
};

/**
 * Récupère le statut de synchronisation
 */
export const getSyncStatus = () => {
  try {
    const stored = localStorage.getItem(SYNC_STATUS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[Background Sync] Erreur lors de la récupération du statut:', error);
    return {};
  }
};

/**
 * Écoute les messages du service worker pour les mises à jour
 */
export const setupBackgroundSyncListener = (onUpdate) => {
  if (!isBackgroundSyncSupported()) {
    return;
  }

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'BACKGROUND_SYNC_UPDATE') {
      console.log('[Background Sync] Mise à jour reçue:', event.data);
      
      // Mettre à jour le statut
      updateSyncStatus(event.data.tag, 'completed', event.data.data);
      
      // Notifier l'application
      if (onUpdate) {
        onUpdate(event.data);
      }
    }
  });
};

/**
 * Nettoie les données de synchronisation anciennes (plus de 24h)
 */
export const cleanupOldSyncData = () => {
  try {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 heures

    // Nettoyer les requêtes échouées anciennes
    const failedRequests = getFailedRequests();
    const recentFailedRequests = failedRequests.filter(req => 
      now - req.timestamp < maxAge
    );
    localStorage.setItem(FAILED_REQUESTS_KEY, JSON.stringify(recentFailedRequests));

    // Nettoyer les favoris en attente anciens
    const pendingFavorites = getPendingFavorites();
    const recentPendingFavorites = pendingFavorites.filter(fav => 
      now - fav.timestamp < maxAge
    );
    localStorage.setItem(PENDING_FAVORITES_KEY, JSON.stringify(recentPendingFavorites));

    console.log('[Background Sync] Nettoyage des données anciennes terminé');
    
  } catch (error) {
    console.error('[Background Sync] Erreur lors du nettoyage:', error);
  }
};
